<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { useSeasonStore } from '@/stores/useSeasonStore';
import { normalizeForSearch } from '@/functions/utils';
import { useConfirm } from '@/composables/useConfirm';

const { open: openConfirm } = useConfirm();

interface BureauMembre {
  role: string;
  prenom: string;
  nom: string;
  telephone: string;
  email: string;
}

interface AffiliationRequest {
  id: number;
  user_email: string | null;
  club_name: string;
  club_id: number | null;
  club_name_linked: string | null;
  club_email: string;
  club_street: string;
  club_complement: string;
  club_postal_code: string;
  club_city: string;
  contact_nom: string;
  contact_street: string;
  contact_complement: string;
  contact_postal_code: string;
  contact_city: string;
  contact_telephone: string;
  bureau: BureauMembre[];
  is_renewal: boolean;
  season: string | null;
  reglement_recu: boolean;
  status: string;
  created_at: string | null;
}

interface Ligue { id: number; code: string; name: string; }
interface Comite { id: number; code: string; name: string; ligue_code: string | null; }

const authStore = useAuthStore();
const seasonStore = useSeasonStore();
const isSuperAdmin = computed(() => authStore.isAuthenticated && authStore.scopes.includes('super_admin'));

const requests = ref<AffiliationRequest[]>([]);
const loading = ref(true);
const statusFilter = ref('');
const seasonFilter = ref(seasonStore.currentSeason);
const listLigueFilter = ref('SDE');
const listComiteFilter = ref('MER');
const search = ref('');

const showDetailModal = ref(false);
const selectedRequest = ref<AffiliationRequest | null>(null);

// Bypass affiliation (super_admin)
const ligues = ref<Ligue[]>([]);
const comites = ref<Comite[]>([]);
const ligueFilter = ref('');
const bypassSeason = ref(getNextSeasonLabel(seasonStore.currentSeason));
const bypassComiteId = ref<number | null>(null);
const bulkApproving = ref(false);

function getNextSeasonLabel(current: string): string {
  const startYear = parseInt(current.split('-')[0], 10);
  return `${startYear + 1}-${startYear + 2}`;
}

const filteredComites = computed(() => {
  if (!ligueFilter.value) return comites.value;
  return comites.value.filter(c => c.ligue_code === ligueFilter.value);
});

const filteredComitesForList = computed(() => {
  if (!listLigueFilter.value) return comites.value;
  return comites.value.filter(c => c.ligue_code === listLigueFilter.value);
});

const availableSeasons = computed(() => {
  const seasons = new Set(requests.value.map(r => r.season).filter(Boolean) as string[]);
  return [...seasons].sort().reverse();
});

const filtered = computed(() => {
  let list = requests.value;
  if (statusFilter.value) list = list.filter(r => r.status === statusFilter.value);
  if (seasonFilter.value) list = list.filter(r => r.season === seasonFilter.value);
  const q = normalizeForSearch(search.value);
  if (q) list = list.filter(r =>
    normalizeForSearch(r.club_name).includes(q) ||
    normalizeForSearch(r.user_email).includes(q)
  );
  return list;
});

const statusLabel = (s: string) =>
  ({ pending: 'En attente', provisionally_approved: 'Activée', approved: 'Approuvée', rejected: 'Rejetée' }[s] ?? s);

const statusClass = (s: string) =>
  ({ pending: 'badge-yellow', provisionally_approved: 'badge-blue', approved: 'badge-green', rejected: 'badge-red' }[s] ?? 'badge-gray');

const load = async () => {
  loading.value = true;
  try {
    const params: Record<string, string> = {};
    if (listComiteFilter.value) params.comite = listComiteFilter.value;
    const { data } = await dartsHubApi.get('/affiliation/admin/requests', { params });
    requests.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

watch(listComiteFilter, load);
watch(listLigueFilter, () => {
  const current = comites.value.find(c => c.code === listComiteFilter.value);
  if (current && current.ligue_code !== listLigueFilter.value) listComiteFilter.value = '';
});
watch(ligueFilter, () => {
  const current = comites.value.find(c => c.id === bypassComiteId.value);
  if (current && current.ligue_code !== ligueFilter.value) bypassComiteId.value = null;
});

const openDetail = (r: AffiliationRequest) => {
  selectedRequest.value = r;
  showDetailModal.value = true;
};

const unapproving = ref(false);

const unapprove = async (r: AffiliationRequest) => {
  if (!(await openConfirm('Dévalider l\'affiliation', `Dévalider l'affiliation de "${r.club_name}" pour la saison ${r.season || ''} ? Le club repassera en statut "Activée".`))) return;
  unapproving.value = true;
  try {
    await dartsHubApi.patch(`/affiliation/admin/requests/${r.id}/unapprove`);
    r.status = 'provisionally_approved';
    toast.success('Affiliation dévalidée.');
    showDetailModal.value = false;
  } catch {
    toast.error('Erreur lors de la dévalidation.');
  } finally {
    unapproving.value = false;
  }
};

const bulkApprove = async () => {
  if (!bypassSeason.value) return;
  const label = bypassComiteId.value
    ? comites.value.find(c => c.id === bypassComiteId.value)?.name ?? 'comité sélectionné'
    : 'tous les clubs';
  if (!(await openConfirm('Valider l\'affiliation', `Valider l'affiliation de ${label} pour la saison ${bypassSeason.value} ?`))) return;
  bulkApproving.value = true;
  try {
    const { data } = await dartsHubApi.post('/affiliation/admin/bulk-approve-season', {
      season: bypassSeason.value,
      comite_id: bypassComiteId.value ?? undefined,
    });
    toast.success(`${data.created} club(s) approuvé(s), ${data.skipped} déjà présent(s)`);
  } catch {
    toast.error('Erreur lors de l\'approbation en masse.');
  } finally {
    bulkApproving.value = false;
  }
};

onMounted(async () => {
  await load();
  if (isSuperAdmin.value) {
    try {
      const [{ data: liguesData }, { data: comitesData }] = await Promise.all([
        dartsHubApi.get('/clubs/ligues'),
        dartsHubApi.get('/clubs/comites'),
      ]);
      ligues.value = liguesData;
      comites.value = comitesData;
    } catch { /* ignore */ }
  }
});
</script>

<template>
  <div class="page">
    <!-- Bypass super_admin -->
    <div v-if="isSuperAdmin" class="bypass-panel">
      <div class="bypass-title"><i class="fa-solid fa-shield-halved" /> Validation directe (super admin)</div>
      <div class="bypass-controls">
        <div class="bypass-field">
          <label>Saison</label>
          <input v-model="bypassSeason" type="text" placeholder="ex: 2026-2027" class="bypass-input" />
        </div>
        <div class="bypass-field">
          <label>Ligue</label>
          <select v-model="ligueFilter" class="bypass-select">
            <option value="">Toutes les ligues</option>
            <option v-for="l in ligues" :value="l.code" :key="l.id">{{ l.name }} ({{ l.code }})</option>
          </select>
        </div>
        <div class="bypass-field">
          <label>Comité</label>
          <select v-model="bypassComiteId" class="bypass-select">
            <option :value="null">Tous les comités</option>
            <option v-for="c in filteredComites" :value="c.id" :key="c.id">{{ c.name }} ({{ c.code }})</option>
          </select>
        </div>
        <button class="btn btn-primary btn-sm" :disabled="bulkApproving || !bypassSeason" @click="bulkApprove">
          <i class="fa-solid fa-check-double" />
          {{ bulkApproving ? 'En cours…' : 'Valider tous les clubs' }}
        </button>
      </div>
    </div>

    <div class="page-header">
      <h1><i class="fa-solid fa-file-signature" /> Demandes d'affiliation</h1>
    </div>

    <div v-if="!loading" class="table-count">
      {{ filtered.length }} demandes
    </div>

    <div class="filters-bar">
      <template v-if="isSuperAdmin && ligues.length">
        <select v-model="listLigueFilter" class="select-sm">
          <option value="">Toutes les ligues</option>
          <option v-for="l in ligues" :value="l.code" :key="l.id">{{ l.name }} ({{ l.code }})</option>
        </select>
        <select v-model="listComiteFilter" class="select-sm">
          <option value="">Tous les comités</option>
          <option v-for="c in filteredComitesForList" :value="c.code" :key="c.id">{{ c.name }} ({{ c.code }})</option>
        </select>
      </template>
      <div class="filter-chips">
        <button class="chip" :class="{ active: statusFilter === '' }" @click="statusFilter = ''">Tous</button>
        <button class="chip chip-yellow" :class="{ active: statusFilter === 'pending' }"
          @click="statusFilter = 'pending'">En
          attente</button>
        <button class="chip chip-blue" :class="{ active: statusFilter === 'provisionally_approved' }"
          @click="statusFilter = 'provisionally_approved'">Activées</button>
        <button class="chip chip-green" :class="{ active: statusFilter === 'approved' }"
          @click="statusFilter = 'approved'">Approuvées</button>
        <button class="chip chip-red" :class="{ active: statusFilter === 'rejected' }"
          @click="statusFilter = 'rejected'">Rejetées</button>
      </div>
      <div v-if="availableSeasons.length" class="filter-chips">
        <button class="chip" :class="{ active: seasonFilter === '' }" @click="seasonFilter = ''">Toutes saisons</button>
        <button v-for="s in availableSeasons" class="chip chip-purple" :class="{ active: seasonFilter === s }" @click="seasonFilter = s"
          :key="s">{{ s }}</button>
      </div>
      <div class="search-bar">
        <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
        <input v-model="search" placeholder="Club ou email…" />
      </div>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Club</th>
            <th>Email demandeur</th>
            <th>Saison</th>
            <th>Type</th>
            <th>Statut</th>
            <th>Club lié</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in filtered" :key="r.id">
            <td style="white-space:nowrap;font-size:0.8rem;color:var(--text-muted)">
              {{ r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '-' }}
            </td>
            <td style="font-weight:500">{{ r.club_name }}</td>
            <td style="font-size:0.85rem">{{ r.user_email || '' }}</td>
            <td style="font-size:0.85rem;white-space:nowrap">{{ r.season || '-' }}</td>
            <td>
              <span class="badge" :class="r.is_renewal ? 'badge-blue' : 'badge-gray'">
                {{ r.is_renewal ? 'Renouvellement' : 'Nouvelle' }}
              </span>
            </td>
            <td>
              <span class="badge" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
            </td>
            <td style="font-size:0.85rem">{{ r.club_name_linked || '' }}</td>
            <td>
              <button class="btn btn-ghost btn-sm" title="Voir détails" @click="openDetail(r)">
                <i class="fa-solid fa-eye" />
              </button>
            </td>
          </tr>
          <tr v-if="filtered.length === 0">
            <td colspan="8" style="text-align:center;color:var(--text-muted)">Aucune demande</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mobile-cards">
      <div v-for="r in filtered" class="mobile-card" :key="r.id">
        <div class="card-top">
          <span style="font-size:0.8rem;color:var(--text-muted)">
            {{ r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '-' }}
          </span>
          <div style="display:flex;gap:0.3rem;flex-wrap:wrap;justify-content:flex-end">
            <span class="badge" :class="r.is_renewal ? 'badge-blue' : 'badge-gray'">
              {{ r.is_renewal ? 'Renouvellement' : 'Nouvelle' }}
            </span>
            <span class="badge" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
          </div>
        </div>
        <div style="font-weight:600;font-size:0.95rem">{{ r.club_name }}</div>
        <div v-if="r.user_email" style="font-size:0.82rem;color:var(--text-muted)">{{ r.user_email }}</div>
        <div v-if="r.season" style="font-size:0.82rem;color:var(--text-muted)">Saison {{ r.season }}</div>
        <div v-if="r.club_name_linked" style="font-size:0.82rem">
          <i class="fa-solid fa-link" style="color:var(--text-muted)" /> {{ r.club_name_linked }}
        </div>
        <div>
          <button class="btn btn-ghost btn-sm" @click="openDetail(r)">
            <i class="fa-solid fa-eye" /> Voir détails
          </button>
        </div>
      </div>
      <div v-if="filtered.length === 0" style="text-align:center;color:var(--text-muted);padding:1.5rem">Aucune demande
      </div>
    </div>

    <!-- Modal détails -->
    <div v-if="showDetailModal && selectedRequest" class="modal-overlay" @click.self="showDetailModal = false">
      <div class="modal modal-xl">
        <div class="modal-header">
          <h2>{{ selectedRequest.club_name }}</h2>
          <button class="btn btn-ghost btn-sm" @click="showDetailModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>

        <div class="info-row">
          <span class="badge" :class="statusClass(selectedRequest.status)">{{ statusLabel(selectedRequest.status)
          }}</span>
          <span class="badge" :class="selectedRequest.is_renewal ? 'badge-blue' : 'badge-gray'">
            {{ selectedRequest.is_renewal ? 'Renouvellement' : 'Nouvelle' }}
          </span>
          <span v-if="selectedRequest.club_name_linked" class="badge badge-green">
            <i class="fa-solid fa-link" /> {{ selectedRequest.club_name_linked }}
          </span>
        </div>

        <div class="detail-grid">
          <div>
            <div class="section-title">Club</div>
            <p>{{ selectedRequest.club_email }}</p>
            <p>{{ selectedRequest.club_street }}{{ selectedRequest.club_complement ? ', ' +
              selectedRequest.club_complement : '' }}</p>
            <p>{{ selectedRequest.club_postal_code }} {{ selectedRequest.club_city }}</p>
          </div>
          <!-- <div>
            <div class="section-title">Contact livraison cibles</div>
            <p>{{ selectedRequest.contact_nom }}</p>
            <p>{{ selectedRequest.contact_street }}{{ selectedRequest.contact_complement ? ', ' + selectedRequest.contact_complement : '' }}</p>
            <p>{{ selectedRequest.contact_postal_code }} {{ selectedRequest.contact_city }}</p>
            <p>{{ selectedRequest.contact_telephone }}</p>
          </div> -->
        </div>

        <div class="section-title" style="margin-top:1rem">Bureau</div>
        <div class="bureau-list">
          <div v-for="(m, i) in selectedRequest.bureau" class="bureau-item" :key="i">
            <div class="bureau-main">
              <span class="bureau-role">{{ m.role }}</span>
              <span class="bureau-name">{{ m.prenom }} {{ m.nom }}</span>
              <span v-if="m.telephone" class="bureau-phone">{{ m.telephone }}</span>
            </div>
            <div v-if="m.email" class="bureau-email">{{ m.email }}</div>
          </div>
        </div>

        <div class="form-actions" style="margin-top:1.25rem">
          <button v-if="selectedRequest.status === 'approved'" class="btn btn-danger btn-sm" :disabled="unapproving"
            @click="unapprove(selectedRequest)">
            <i class="fa-solid fa-rotate-left" />
            {{ unapproving ? 'En cours…' : 'Dévalider' }}
          </button>
          <button class="btn btn-ghost btn-sm" @click="showDetailModal = false">Fermer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bypass-panel {
  background: rgba(234, 179, 8, 0.08);
  border: 1px solid rgba(234, 179, 8, 0.35);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
}

.bypass-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #92400e;
  margin-bottom: 0.75rem;
}

.bypass-controls {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.bypass-filter-row {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(234, 179, 8, 0.2);
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filters-bar {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.filter-chips {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.chip {
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.76rem;
  font-weight: 600;
  border: 1px solid var(--border);
  background: var(--bg-input);
  color: var(--text);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  white-space: nowrap;
}

.chip.active {
  background: var(--text);
  color: var(--bg);
  border-color: var(--text);
}

.chip-yellow.active {
  background: #b45309;
  border-color: #b45309;
  color: #fff;
}

.chip-yellow:not(.active):hover {
  background: rgba(234, 179, 8, 0.12);
  border-color: rgba(234, 179, 8, 0.4);
  color: #b45309;
}

.chip-blue.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.chip-blue:not(.active):hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.4);
  color: #2563eb;
}

.chip-green.active {
  background: #15803d;
  border-color: #15803d;
  color: #fff;
}

.chip-green:not(.active):hover {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.4);
  color: #15803d;
}

.chip-red.active {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}

.chip-red:not(.active):hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.4);
  color: #dc2626;
}

.chip-purple.active {
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;
}

.chip-purple:not(.active):hover {
  background: rgba(124, 58, 237, 0.1);
  border-color: rgba(124, 58, 237, 0.4);
  color: #7c3aed;
}

.bypass-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.bypass-field label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bypass-input,
.bypass-select {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-size: 0.875rem;
  padding: 0.35rem 0.6rem;
  min-width: 160px;
}

.select-sm {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-size: 0.875rem;
  padding: 0.35rem 0.6rem;
}

.table-count {
  margin-top: 0.75rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 100;
  overflow-y: auto;
  padding: 1.5rem 1rem;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  min-width: 380px;
  max-width: 520px;
  width: 90%;
  margin: auto 0;
}

.modal-xl {
  max-width: 760px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.info-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.4rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.detail-grid p {
  margin: 0.15rem 0;
  font-size: 0.9rem;
}

.bureau-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.bureau-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
}

.bureau-item:last-child {
  border-bottom: none;
}

.bureau-main {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  font-size: 0.88rem;
}

.bureau-role {
  font-weight: 600;
  color: var(--text);
}

.bureau-name {
  color: var(--text);
}

.bureau-phone {
  color: var(--text-muted);
  font-size: 0.82rem;
}

.bureau-email {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.15rem;
  padding-left: 0;
}

.badge-yellow {
  background: rgba(234, 179, 8, 0.15);
  color: #ca8a04;
  border: 1px solid rgba(234, 179, 8, 0.3);
}

.badge-blue {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.mobile-cards {
  display: none;
}

@media (max-width: 640px) {
  .table-wrapper {
    display: none;
  }

  .mobile-cards {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}
</style>
